import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserCircle, LogIn, UserPlus, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LoginPromptModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(20, 41, 31, 0.4)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '24px'
    },
    modal: {
      width: '100%',
      maxWidth: '440px',
      backgroundColor: '#ffffff',
      borderRadius: '24px',
      padding: '32px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
      position: 'relative',
      textAlign: 'center'
    },
    iconContainer: {
      width: '64px',
      height: '64px',
      borderRadius: '20px',
      backgroundColor: '#f0fdf4',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 24px auto',
      color: '#14291F'
    },
    title: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '12px',
      letterSpacing: '-0.02em'
    },
    message: {
      fontSize: '15px',
      color: '#6b7280',
      lineHeight: '1.6',
      marginBottom: '32px'
    },
    actions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    },
    loginBtn: {
      width: '100%',
      padding: '14px',
      borderRadius: '12px',
      border: 'none',
      backgroundColor: '#14291F',
      color: '#ffffff',
      fontSize: '15px',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'all 0.2s ease'
    },
    signupBtn: {
      width: '100%',
      padding: '14px',
      borderRadius: '12px',
      border: '1.5px solid #e5e7eb',
      backgroundColor: '#ffffff',
      color: '#374151',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'all 0.2s ease'
    },
    closeBtn: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      color: '#9ca3af',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          style={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div 
            style={styles.modal}
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
          >
            <button style={styles.closeBtn} onClick={onClose}>
              <X size={20} />
            </button>

            <div style={styles.iconContainer}>
              <UserCircle size={32} />
            </div>

            <h3 style={styles.title}>Login Required</h3>
            <p style={styles.message}>
              Please log in or create an account to proceed with your checkout and track your orders.
            </p>

            <div style={styles.actions}>
              <motion.button 
                whileHover={{ backgroundColor: '#0c1a14', scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                style={styles.loginBtn} 
                onClick={() => navigate('/login')}
              >
                <LogIn size={18} />
                Sign In
              </motion.button>
              
              <motion.button 
                whileHover={{ backgroundColor: '#f9fafb', scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                style={styles.signupBtn} 
                onClick={() => navigate('/signup')}
              >
                <UserPlus size={18} />
                Create Account
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoginPromptModal;
